import { ApolloClient } from '@apollo/client';
import {
  GridColDef,
  GridColumns,
  GridColumnVisibilityModel,
  GridDensity,
  GridFilterModel,
  GridSortModel,
} from '@mui/x-data-grid-pro';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { isEqual } from 'lodash';
import {
  useGetMeQuery,
  useUpdateUserConfigurationMutation,
} from '../generated/graphql';
import { TABLE_NAMES } from '../lib/constants';
import { getRandomNumber } from '../lib/date';
import { LoadingAndError } from '../utils/LoadingAndError';

interface AttachConfiguration {
  (
    columns: GridColDef[],
    columnsConfiguration?: ColumnsConfiguration,
  ): GridColumns;
}

interface GetColumnsConfiguration {
  (tableName: string): {
    [column: string]: Omit<GridColDef, 'field'>;
  };
}

interface GetFilterModel {
  (tableName: string): GridFilterModel;
}

interface GetFixedConfiguration {
  (tableName: string): TableConfiguration;
}

interface GetSortModel {
  (tableName: string): GridSortModel;
}

interface GetCustomConfiguration {
  (tableName: string, key: string): unknown | undefined;
}

interface GetGridDensity {
  (tableName: string): GridDensity;
}

interface UpdateColumnConfiguration {
  (
    tableName: string,
    column: string,
    configuration: Omit<GridColDef, 'field'>,
  ): void;
}

interface UpdateBulkVisibilityConfiguration {
  (tableName: string, columns: string[], newVisibility: boolean): void;
}

interface UpdateColumnVisibilityModelConfiguration {
  (tableName: string, columnVisibilityModel: GridColumnVisibilityModel): void;
}

interface UpdateFilterConfiguration {
  (tableName: string, filterModel: GridFilterModel): void;
}

interface UpdateSortConfiguration {
  (tableName: string, sortModel: GridSortModel): void;
}

interface UpdateColumnOrderConfiguration {
  (tableName: string, columnOrder: string[]): void;
}

interface UpdateCustomConfiguration {
  (tableName: string, key: string, value: unknown): void;
}

interface UpdateGridDensity {
  (tableName: string, density: GridDensity): void;
}

interface UpdateConfiguration {
  (
    tableName: string,
    key: keyof Configuration[string],
    value: Configuration[string][keyof Configuration[string]],
  ): void;
}

interface GetTFTTime {
  (isStartTime: boolean): string;
}
interface GetTFTSameDay {
  (): boolean;
}

interface GetMasterDate {
  (): string;
}

interface GetReportToTerminalView {
  (): {
    startDate: string;
    locationId: number;
    viewObj: {
      routeView: boolean;
      gridView: boolean;
      valueBoxesView: boolean;
      infoView: boolean;
    };
  };
}
interface SetMasterDate {
  (stringFullDate: string, isTouchTime: boolean, isSameDay?: boolean): void;
}
interface SetTrafficTFTTime {
  (tftTime: string): void;
}
interface SetReportToTerminalView {
  (
    startDate: string,
    terminalId: number,
    viewObj: {
      routeView: boolean;
      gridView: boolean;
      valueBoxesView: boolean;
      infoView: boolean;
    },
  ): void;
}

interface ClearConfiguration {
  (): void;
}

interface Department {
  id: string; //'no' | 'se' | 'dk'
  override: boolean;
}

interface UserConfigurationContextValues {
  attachConfiguration?: AttachConfiguration;
  updateColumnConfiguration?: UpdateColumnConfiguration;
  updateColumnVisibilityConfiguration?: UpdateColumnConfiguration;
  updateBulkVisibilityConfiguration?: UpdateBulkVisibilityConfiguration;
  updateColumnVisibilityModelConfiguration?: UpdateColumnVisibilityModelConfiguration;
  updateFilterConfiguration?: UpdateFilterConfiguration;
  updateConfiguration?: UpdateConfiguration;
  updateSortConfiguration?: UpdateSortConfiguration;
  updateColumnOrderConfiguration?: UpdateColumnOrderConfiguration;
  updateCustomConfiguration?: UpdateCustomConfiguration;
  updateGridDensity?: UpdateGridDensity;
  clearConfiguration?: ClearConfiguration;
  getColumnsConfiguration?: GetColumnsConfiguration;
  getFixedRoutesConfiguration?: GetFixedConfiguration;
  getFilterModel?: GetFilterModel;
  getSortModel?: GetSortModel;
  getCustomConfiguration?: GetCustomConfiguration;
  getGridDensity?: GetGridDensity;
  department?: Department;
  location?: any;
  hasTrafficAccess?: boolean;
  hasTerminalAccess?: boolean;
  hasCustomsAccess?: boolean;
  isAdmin?: boolean;
  myUserId?: string;
  getTFTTime?: GetTFTTime;
  getTFTReadOnlyTime?: GetTFTTime;
  getTFTSameDay?: GetTFTSameDay;
  getMasterDate?: GetMasterDate;
  setMasterDate?: SetMasterDate;
  setMasterDateForTFTReadOnly?: SetTrafficTFTTime;
  setReportToTerminalView?: SetReportToTerminalView;
  getReportToTerminalView?: GetReportToTerminalView;
  globalDateFormat?: string;
  setGlobalDateFormat?: React.Dispatch<React.SetStateAction<string>>;
}

const UserConfigurationContext = createContext<UserConfigurationContextValues>({
  attachConfiguration: undefined,
  updateColumnConfiguration: undefined,
  updateColumnVisibilityConfiguration: undefined,
  updateBulkVisibilityConfiguration: undefined,
  updateColumnVisibilityModelConfiguration: undefined,
  updateFilterConfiguration: undefined,
  updateConfiguration: undefined,
  updateCustomConfiguration: undefined,
  updateGridDensity: undefined,
  clearConfiguration: undefined,
  getColumnsConfiguration: undefined,
  getFixedRoutesConfiguration: undefined,
  getFilterModel: undefined,
  getSortModel: undefined,
  getCustomConfiguration: undefined,
  getGridDensity: undefined,
  department: undefined,
  hasTerminalAccess: undefined,
  hasCustomsAccess: undefined,
  hasTrafficAccess: undefined,
  isAdmin: undefined,
  myUserId: undefined,
  getTFTTime: undefined,
  getTFTReadOnlyTime: undefined,
  getTFTSameDay: undefined,
  getMasterDate: undefined,
  setMasterDate: undefined,
  setMasterDateForTFTReadOnly: undefined,
  setReportToTerminalView: undefined,
  getReportToTerminalView: undefined,
  globalDateFormat: undefined,
  setGlobalDateFormat: undefined,
});

interface ColumnsConfiguration {
  [column: string]: Omit<GridColDef, 'field'>;
}

interface TableConfiguration {
  id?: number;
  filterModel: GridFilterModel;
  sortModel: GridSortModel;
  columnOrder: string[];
  columns: ColumnsConfiguration;
  custom: {
    [key: string]: unknown;
  };
  density: GridDensity;
  globalDateFormat: string;
}

interface Configuration {
  [tableName: string]: TableConfiguration;
}

interface UserConfigurationProviderProps {
  children: ReactNode;
  client?: ApolloClient<unknown>;
}

export function UserConfigurationProvider({
  children,
}: UserConfigurationProviderProps) {
  const { data, loading, error } = useGetMeQuery();
  const [updateUserConfiguration] = useUpdateUserConfigurationMutation();
  const tableListToNotAutoUpdateconfig = [
    TABLE_NAMES.TFTReadOnly,
    TABLE_NAMES.NewShipmentLoadingList,
    TABLE_NAMES.TruckFillAndTime,
    TABLE_NAMES.MatchingShipment,
  ];

  const [configurationState, setConfigurationState] = useState<{
    id: number;
    configuration: Configuration;
    isClearConfig?: boolean;
  }>({ id: 0, configuration: {} });

  const [globalDateFormat, setGlobalDateFormat] =
    useState<string>('yyyy-MM-dd HH:mm');

  const setConfiguration = (
    newConfiguration: Configuration,
    clearConfig = false,
  ) => {
    if (clearConfig) {
      updateUserConfiguration({
        variables: {
          input: {
            configuration: {},
          },
        },
      }).then(() => {
        window.location.reload();
      });
    }

    const previousConfiguration = configurationState.configuration ?? {};
    const nextConfiguration = newConfiguration ?? {};
    const changedTableNames = Array.from(
      new Set([
        ...Object.keys(previousConfiguration),
        ...Object.keys(nextConfiguration),
      ]),
    ).filter(
      (tableName) =>
        !isEqual(
          previousConfiguration[tableName],
          nextConfiguration[tableName],
        ),
    );

    const newConfigurationState = {
      id: changedTableNames.some((tableName) =>
        tableListToNotAutoUpdateconfig.includes(tableName),
      )
        ? configurationState.id
        : configurationState.id + 1,
      configuration: nextConfiguration,
      isClearConfig: clearConfig,
    };
    setConfigurationState(newConfigurationState);
  };

  //new for column visibility check
  const setVisibilityConfiguration = (newConfiguration: Configuration) => {
    const newConfigurationState = {
      id: getRandomNumber(configurationState.id),
      configuration: newConfiguration ?? {},
    };
    setConfigurationState(newConfigurationState);
  };

  const department = {
    override: false,
    id: 'se',
  };
  const [isConfigurationSet, setIsConfigurationSet] = useState(false);

  useEffect(() => {
    if (data != null && !isConfigurationSet) {
      setConfiguration(data?.me.configuration);
      setIsConfigurationSet(true);
      const userDateTimeFormatSetting =
        data?.me.configuration?.userDateTimeFormatSetting;
      if (
        userDateTimeFormatSetting &&
        userDateTimeFormatSetting.globalDateFormat
      ) {
        setGlobalDateFormat(userDateTimeFormatSetting.globalDateFormat);
      }
    }
  }, [data]);

  useEffect(() => {
    if (
      data != null &&
      isConfigurationSet &&
      !configurationState.isClearConfig
    ) {
      updateUserConfiguration({
        variables: {
          input: {
            configuration: configurationState.configuration,
          },
        },
        optimisticResponse: {
          updateConfiguration: {
            ...data?.me,
            __typename: 'PublicUser',
            configuration: configurationState.configuration,
          },
        },
      });
    }
  }, [configurationState.id]);

  const attachConfiguration = (
    columns: GridColDef[],
    columnsConfiguration?: ColumnsConfiguration,
  ) => {
    if (columnsConfiguration == null) {
      return columns;
    }

    return columns.map((column) => {
      const columnConfiguration = columnsConfiguration[column.field];

      if (columnConfiguration == null) {
        return column;
      }

      return { ...column, ...columnConfiguration };
    });
  };

  const getFilterModel = useCallback(
    (tableName: string): GridFilterModel => {
      return configurationState.configuration[tableName]?.filterModel;
    },
    [configurationState.configuration],
  );

  const getCustomConfiguration = useCallback(
    (tableName: string, key: string) => {
      const tableConfiguration = configurationState.configuration[tableName];
      if (tableConfiguration == null) {
        return undefined;
      }
      const customConfiguration = tableConfiguration.custom;

      if (customConfiguration == null) {
        return undefined;
      }

      return customConfiguration[key];
    },
    [configurationState.configuration],
  );

  const getGridDensity = useCallback(
    (tableName: string): GridDensity => {
      return configurationState.configuration[tableName]?.density;
    },
    [configurationState.configuration],
  );

  const getSortModel = useCallback(
    (tableName: string): GridSortModel => {
      return configurationState.configuration[tableName]?.sortModel;
    },
    [configurationState.configuration],
  );

  const getColumnsConfiguration = useCallback(
    (tableName: string): ColumnsConfiguration => {
      return configurationState.configuration[tableName]?.columns;
    },
    [configurationState.configuration],
  );

  const getFixedRoutesConfiguration = useCallback(
    (tableName: string): TableConfiguration => {
      return configurationState.configuration[tableName];
    },
    [configurationState.configuration],
  );

  const updateColumnConfiguration = useCallback(
    (
      tableName: string,
      column: string,
      newColumnConfiguration: Omit<GridColDef, 'field'>,
    ) => {
      const userConfiguration = configurationState.configuration ?? {};
      const tableConfiguration = userConfiguration[tableName] ?? {
        columns: {},
      };
      const tableColumnsConfiguration = tableConfiguration.columns ?? {};

      const currentColumnConfiguration =
        tableColumnsConfiguration[column] ?? {};

      const columnConfiguration = {
        ...currentColumnConfiguration,
        ...newColumnConfiguration,
      };

      const newUserConfiguration = {
        ...userConfiguration,
        [tableName]: {
          ...tableConfiguration,
          columns: {
            ...tableConfiguration.columns,
            [column]: columnConfiguration,
          },
        },
      };

      setConfiguration(newUserConfiguration);
    },
    [configurationState.configuration],
  );

  //For column visibility
  const updateColumnVisibilityConfiguration = useCallback(
    (
      tableName: string,
      column: string,
      newColumnConfiguration: Omit<GridColDef, 'field'>,
    ) => {
      const userConfiguration = configurationState.configuration ?? {};

      const tableConfiguration = userConfiguration[tableName] ?? {
        columns: {},
      };

      const tableColumnsConfiguration = tableConfiguration.columns ?? {};

      const currentColumnConfiguration =
        tableColumnsConfiguration[column] ?? {};

      const columnConfiguration = {
        ...currentColumnConfiguration,
        ...newColumnConfiguration,
      };

      const newUserConfiguration = {
        ...userConfiguration,
        [tableName]: {
          ...tableConfiguration,
          columns: {
            ...tableConfiguration.columns,
            [column]: columnConfiguration,
          },
        },
      };
      setVisibilityConfiguration(newUserConfiguration);
    },
    [configurationState.configuration],
  );

  const updateBulkVisibilityConfiguration = useCallback(
    (tableName: string, columns: string[], isVisible: boolean) => {
      const userConfiguration = configurationState.configuration ?? {};
      const tableConfiguration = userConfiguration[tableName] ?? {
        columns: {},
      };
      const tableColumnsConfiguration = tableConfiguration.columns ?? {};
      let newTableColumnsConfiguration: ColumnsConfiguration =
        tableConfiguration.columns ?? {};

      if (columns && columns.length > 0) {
        columns.map((col) => {
          const currentColumnConfiguration =
            tableColumnsConfiguration[col] ?? {};

          const columnConfig = {
            ...currentColumnConfiguration,
            hide: !isVisible,
          };

          newTableColumnsConfiguration = {
            ...newTableColumnsConfiguration,
            [col]: columnConfig,
          };
        });
      }

      const newUserConfiguration = {
        ...userConfiguration,
        [tableName]: {
          ...tableConfiguration,
          columns: newTableColumnsConfiguration,
        },
      };
      setVisibilityConfiguration(newUserConfiguration);
    },
    [configurationState.configuration],
  );

  const updateColumnVisibilityModelConfiguration = useCallback(
    (tableName: string, columnVisibilityModel: GridColumnVisibilityModel) => {
      const userConfiguration = configurationState.configuration ?? {};
      const tableConfiguration = userConfiguration[tableName] ?? {
        columns: {},
      };
      const tableColumnsConfiguration = tableConfiguration.columns ?? {};
      const relevantColumns = Array.from(
        new Set([
          ...Object.keys(tableColumnsConfiguration),
          ...Object.keys(columnVisibilityModel),
        ]),
      );

      let newTableColumnsConfiguration: ColumnsConfiguration = {
        ...tableColumnsConfiguration,
      };

      relevantColumns.forEach((column) => {
        const currentColumnConfiguration =
          tableColumnsConfiguration[column] ?? {};
        const isVisible = columnVisibilityModel[column] !== false;

        newTableColumnsConfiguration = {
          ...newTableColumnsConfiguration,
          [column]: {
            ...currentColumnConfiguration,
            hide: !isVisible,
          },
        };
      });

      const newUserConfiguration = {
        ...userConfiguration,
        [tableName]: {
          ...tableConfiguration,
          columns: newTableColumnsConfiguration,
        },
      };

      setVisibilityConfiguration(newUserConfiguration);
    },
    [configurationState.configuration],
  );

  const updateConfiguration = useCallback(
    <T extends keyof Configuration[string]>(
      tableName: string,
      key: keyof Configuration[string],
      value: Configuration[string][T],
    ) => {
      const userConfiguration = configurationState.configuration ?? {};
      const tableConfiguration = userConfiguration[tableName] ?? {};

      const newUserConfiguration = {
        ...userConfiguration,
        [tableName]: {
          ...tableConfiguration,
          [key]: value,
        },
      };
      setConfiguration(newUserConfiguration);
    },
    [configurationState.configuration],
  );

  const updateFilterConfiguration = (
    tableName: string,
    filterModel: GridFilterModel,
  ) => {
    updateConfiguration(tableName, 'filterModel', filterModel);
  };

  const updateSortConfiguration = (
    tableName: string,
    sortModel: GridSortModel,
  ) => {
    updateConfiguration(tableName, 'sortModel', sortModel);
  };

  const updateColumnOrderConfiguration = (
    tableName: string,
    columnOrder: string[],
  ) => {
    updateConfiguration(tableName, 'columnOrder', columnOrder);
  };

  const updateGridDensity = (tableName: string, density: GridDensity) => {
    updateConfiguration(tableName, 'density', density);
  };

  const getTFTTime = (isStartTime: boolean): string => {
    const value = getCustomConfiguration(
      TABLE_NAMES.TruckFillAndTime,
      'tftTime',
    );
    if (value && typeof value === 'string') {
      return isStartTime ? value.split('_')[0] : value.split('_')[1];
    } else {
      return isStartTime ? '00:00' : '23:59';
    }
  };

  const getTFTReadOnlyTime = (isStartTime: boolean): string => {
    const value = getCustomConfiguration(TABLE_NAMES.TFTReadOnly, 'tftTime');
    if (value && typeof value === 'string') {
      return isStartTime ? value.split('_')[0] : value.split('_')[1];
    } else {
      return isStartTime ? '00:00' : '23:59';
    }
  };

  const getTFTSameDay = (): boolean => {
    const value = getCustomConfiguration(
      TABLE_NAMES.TruckFillAndTime,
      'isSameDay',
    );
    if (value && typeof value === 'boolean') {
      return value;
    }
    return false;
  };

  const setReportToTerminalView = (
    startDate: string,
    terminalId: number,
    viewObj: {
      routeView: boolean;
      gridView: boolean;
      valueBoxesView: boolean;
      infoView: boolean;
    },
  ) => {
    const newCustomConfiguration = {
      ...(configurationState.configuration[TABLE_NAMES.TFTReadOnly]?.custom ??
        {}),
      ['date']: startDate,
      ['routeView']: viewObj.routeView,
      ['gridView']: viewObj.gridView,
      ['valueBoxesView']: viewObj.valueBoxesView,
      ['infoView']: viewObj.infoView,
      ['terminalId']: terminalId,
    };
    updateConfiguration(
      TABLE_NAMES.TFTReadOnly,
      'custom',
      newCustomConfiguration,
    );
  };

  const setMasterDate = (
    stringFullDate: string,
    isTouchTime: boolean,
    isSameDay?: boolean,
  ) => {
    //stringFullDate -> yyyy-MM-dd HH:mm#yyyy-MM-dd HH:mm

    const startDate = `${stringFullDate.split('#')[0].split(' ')[0]}`;
    const startTime = `${stringFullDate.split('#')[0].split(' ')[1]}`;
    const endDate = `${stringFullDate.split('#')[1].split(' ')[0]}`;
    const endTime = `${stringFullDate.split('#')[1].split(' ')[1]}`;

    if (isTouchTime && isSameDay != undefined) {
      updateTftDateAndDayConfiguration(
        'master_date',
        `${startDate}#${endDate}`,
        'tftTime',
        `${startTime}_${endTime}`,
        isSameDay,
      );
    } else if (isTouchTime) {
      updateTftDateConfiguration(
        'master_date',
        `${startDate}#${endDate}`,
        'tftTime',
        `${startTime}_${endTime}`,
      );
    } else {
      updateCustomConfiguration(
        TABLE_NAMES.TruckFillAndTime,
        'master_date',
        `${startDate}#${endDate}`,
      );
    }
  };

  const setMasterDateForTFTReadOnly = (stringFullDate: string) => {
    //stringFullDate -> yyyy-MM-dd HH:mm#yyyy-MM-dd HH:mm
    const startTime = `${stringFullDate.split('#')[0].split(' ')[1]}`;
    const endTime = `${stringFullDate.split('#')[1].split(' ')[1]}`;
    updateTFTReadOnlyDateConfiguration('tftTime', `${startTime}_${endTime}`);
  };

  const getMasterDate = (): string => {
    const value = getCustomConfiguration(
      TABLE_NAMES.TruckFillAndTime,
      'master_date',
    );
    if (value && typeof value === 'string') {
      return value;
    } else {
      return '-';
    }
  };

  const getReportToTerminalView = (): {
    startDate: string;
    locationId: number;
    viewObj: {
      routeView: boolean;
      gridView: boolean;
      valueBoxesView: boolean;
      infoView: boolean;
    };
  } => {
    const startDate = getCustomConfiguration(TABLE_NAMES.TFTReadOnly, 'date');
    const gridView = getCustomConfiguration(
      TABLE_NAMES.TFTReadOnly,
      'gridView',
    );
    const locationId = getCustomConfiguration(
      TABLE_NAMES.TFTReadOnly,
      'locationId',
    );
    const infoView = getCustomConfiguration(
      TABLE_NAMES.TFTReadOnly,
      'infoView',
    );
    const routeView = getCustomConfiguration(
      TABLE_NAMES.TFTReadOnly,
      'routeView',
    );
    const valueBoxesView = getCustomConfiguration(
      TABLE_NAMES.TFTReadOnly,
      'valueBoxesView',
    );
    const viewObj = {
      gridView: typeof gridView === 'boolean' ? gridView : false,
      infoView: typeof infoView === 'boolean' ? infoView : false,
      routeView: typeof routeView === 'boolean' ? routeView : false,
      valueBoxesView:
        typeof valueBoxesView === 'boolean' ? valueBoxesView : false,
    };
    return {
      startDate: typeof startDate === 'string' ? startDate : '',
      locationId: typeof locationId === 'number' ? locationId : 0,
      viewObj: viewObj,
    };
  };

  const updateCustomConfiguration = (
    tableName: string,
    key: string,
    value: unknown,
  ) => {
    const newCustomConfiguration = {
      ...(configurationState.configuration[tableName]?.custom ?? {}),
      [key]: value,
    };
    updateConfiguration(tableName, 'custom', newCustomConfiguration);
  };

  const updateTftDateConfiguration = (
    masterDate: string,
    dateValue: string,
    tftTime: string,
    timeValue: string,
  ) => {
    const newCustomConfiguration = {
      ...(configurationState.configuration[TABLE_NAMES.TruckFillAndTime]
        ?.custom ?? {}),
      [masterDate]: dateValue,
      [tftTime]: timeValue,
    };
    updateConfiguration(
      TABLE_NAMES.TruckFillAndTime,
      'custom',
      newCustomConfiguration,
    );
  };

  const updateTFTReadOnlyDateConfiguration = (
    tftTime: string,
    timeValue: string,
  ) => {
    const newCustomConfiguration = {
      ...(configurationState.configuration[TABLE_NAMES.TFTReadOnly]?.custom ??
        {}),
      [tftTime]: timeValue,
    };
    updateConfiguration(
      TABLE_NAMES.TFTReadOnly,
      'custom',
      newCustomConfiguration,
    );
  };

  const updateTftDateAndDayConfiguration = (
    masterDate: string,
    dateValue: string,
    tftTime: string,
    timeValue: string,
    sameDay: boolean,
  ) => {
    const newCustomConfiguration = {
      ...(configurationState.configuration[TABLE_NAMES.TruckFillAndTime]
        ?.custom ?? {}),
      [masterDate]: dateValue,
      [tftTime]: timeValue,
      ['isSameDay']: sameDay,
    };
    updateConfiguration(
      TABLE_NAMES.TruckFillAndTime,
      'custom',
      newCustomConfiguration,
    );
  };

  const clearConfiguration = useCallback(() => {
    setConfiguration({}, true);
  }, []);

  return (
    <LoadingAndError data={data} loading={loading} error={error}>
      {() => (
        <UserConfigurationContext.Provider
          value={{
            attachConfiguration,
            getColumnsConfiguration,
            getFilterModel,
            getSortModel,
            getCustomConfiguration,
            getGridDensity,
            getFixedRoutesConfiguration,
            updateColumnConfiguration,
            updateColumnVisibilityConfiguration,
            updateBulkVisibilityConfiguration,
            updateColumnVisibilityModelConfiguration,
            updateGridDensity,
            updateFilterConfiguration,
            updateSortConfiguration,
            updateColumnOrderConfiguration,
            updateCustomConfiguration,
            clearConfiguration,
            department,
            getTFTTime,
            getTFTReadOnlyTime,
            getTFTSameDay,
            setMasterDate,
            setMasterDateForTFTReadOnly,
            updateConfiguration,
            getMasterDate,
            getReportToTerminalView,
            hasTerminalAccess: data?.me.terminalAccess,
            hasCustomsAccess: data?.me.customsAccess,
            hasTrafficAccess: data?.me.trafficAccess,
            isAdmin: data?.me.admin,
            myUserId: data?.me.id ?? '',
            setReportToTerminalView,
            globalDateFormat,
            setGlobalDateFormat,
            location: data?.me.location,
          }}
        >
          {children}
        </UserConfigurationContext.Provider>
      )}
    </LoadingAndError>
  );
}

export function useUserConfiguration() {
  const context = useContext(UserConfigurationContext);
  if (context === undefined) {
    throw new Error(
      'useUserConfiguration must be used within a UserConfigurationProvider',
    );
  }

  return context as {
    attachConfiguration: AttachConfiguration;
    filterModel: GridFilterModel;
    getFilterModel: GetFilterModel;
    getSortModel: GetSortModel;
    getCustomConfiguration: GetCustomConfiguration;
    getColumnsConfiguration: GetColumnsConfiguration;
    getFixedRoutesConfiguration: GetFixedConfiguration;
    getGridDensity: GetGridDensity;
    updateColumnConfiguration: UpdateColumnConfiguration;
    updateColumnVisibilityConfiguration: UpdateColumnConfiguration;
    updateBulkVisibilityConfiguration: UpdateBulkVisibilityConfiguration;
    updateColumnVisibilityModelConfiguration: UpdateColumnVisibilityModelConfiguration;
    updateFilterConfiguration: UpdateFilterConfiguration;
    updateConfiguration: UpdateConfiguration;
    updateSortConfiguration: UpdateSortConfiguration;
    updateColumnOrderConfiguration: UpdateColumnOrderConfiguration;
    updateCustomConfiguration: UpdateCustomConfiguration;
    updateGridDensity: UpdateGridDensity;
    clearConfiguration: ClearConfiguration;
    hasTerminalAccess: boolean;
    hasCustomsAccess: boolean;
    hasTrafficAccess: boolean;
    isAdmin: boolean;
    myUserId: string;
    department: Department;
    location: { id?: number | string; name?: string };
    getTFTTime: GetTFTTime;
    getTFTReadOnlyTime: GetTFTTime;
    getTFTSameDay: GetTFTSameDay;
    setMasterDate: SetMasterDate;
    setMasterDateForTFTReadOnly: SetTrafficTFTTime;
    getMasterDate: GetMasterDate;
    setReportToTerminalView: SetReportToTerminalView;
    getReportToTerminalView: GetReportToTerminalView;
    globalDateFormat: string;
    setGlobalDateFormat: React.Dispatch<React.SetStateAction<string>>;
  };
}
